import ActDomainForm from 'civil-status/components/domain-management/form';
import { EditActDomainManagementCrumbs } from 'civil-status/constants/breadcrumb';
import { memo, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ActDomainManagement } from 'shell/models/act-domain-management';
import { HttpResponse, NewHttpResponseList } from 'shell/models/http-response';
import { transformActDomain } from '../utils/transform-act-domain';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { useEventCallback } from '@mui/material';
import { transformRequestActDomain } from '../utils/transform-act-domain-request';
import { AxiosPromise } from 'axios';
import { toast } from 'react-toastify';
import { ValueDomainType } from 'shell/models/value-domain-type';
import { transformActDomainTypeValues } from '../utils/transform-act-domain-type-values';
import { transformDataset } from 'civil-status/components/domain-management/utils';
import { ActDomainFormVM } from 'civil-status/view-models/act-domain-form';
import {
  ActValueDomainResponseVM,
  ActValueDomainVM,
} from 'civil-status/view-models/act-value-domain-managment-list';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useUserProfile } from 'shell/hooks/use-user-profile';

const EditActDomainManagementPage = (): React.ReactElement => {
  const params = useParams();
  const navigate = useNavigate();
  const [actDomain] = useHttpFetch<HttpResponse<ActDomainManagement>, ActDomainFormVM>(
    `${__ACT_API_URL__}/tipo-dominio-atti/${params.id}`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: transformActDomain,
    }
  );

  const onCancel = () => {
    navigate('/stato-civile/ricerca-dominio');
  };

  const saveActDomain = useHttpClient(`${__ACT_API_URL__}/tipo-dominio-atti/update`, {
    options: {
      method: 'PUT',
      loading: true,
      errorCatching: true,
    },
  });

  const onSubmit = useEventCallback((values: ActDomainFormVM) => {
    const data = transformRequestActDomain(values);

    (
      saveActDomain({
        data,
      }) as AxiosPromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      navigate('/stato-civile/ricerca-dominio');
    });
  });

  const [valuesDomain, getValuesDomain] = useHttpFetch<
    NewHttpResponseList<ValueDomainType>,
    ActValueDomainResponseVM
  >(`${__ACT_API_URL__}/valori-dominio-atti`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: transformActDomainTypeValues,
  });

  const valuesDomainList = useMemo(() => {
    return ((valuesDomain?.data as ActValueDomainResponseVM)?.valuesDomain ||
      []) as Array<ActValueDomainVM>;
  }, [valuesDomain?.data]);

  useEffect(() => {
    if (params?.id) {
      const queryString = transformDataset({ actDomainId: params?.id, withoutPagination: true });
      getValuesDomain({
        params: {},
        paramsSerializer: () => queryString,
      });
    }
  }, [params?.id]);

  const { userProfile } = useUserProfile();

  const initialValues: ActDomainFormVM = useMemo(() => {
    const actDomainData = (actDomain?.data || {}) as ActDomainFormVM;
    return {
      ...actDomainData,
      actDomainValues: valuesDomainList,
      entityBody: actDomainData?.entityBody || userProfile.datiLogin?.key?.codiceEnte,
    };
  }, [actDomain?.data, valuesDomainList, userProfile.datiLogin]);

  const valuesDomainListCount = useMemo(() => {
    return (valuesDomain?.data as ActValueDomainResponseVM)?.pageInfo?.totalCount || 0;
  }, [valuesDomain?.data]);

  return (
    <>
      <Breadcrumbs items={EditActDomainManagementCrumbs} />
      <ActDomainForm
        title="Modifica dominio"
        initialValues={initialValues}
        valuesDomainListCount={valuesDomainListCount}
        isForEdit
        onSubmit={onSubmit}
        onCancel={onCancel}
      />
    </>
  );
};

EditActDomainManagementPage.displayName = 'Detail Act Management Page';
const ActEditDomainManagementPage = memo(EditActDomainManagementPage);
export default ActEditDomainManagementPage;
